import { Fragment, useState, useEffect, useRef } from "react";
import {
  Outlet,
  useLocation,
  Link,
  Form,
  useRouteLoaderData,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleSidebar,
  updateUserStatus,
} from "../../store/redux_auth/authSlice";
import FilingNotificationService from "../../services/sockets/notifications/FilingNotificationService";
import LicenseValidationNotificationService from "../../services/sockets/notifications/LicenseValidationNotificationService";
import AffiliateNotificationService from "../../services/sockets/notifications/AffiliateNotificationService";
import { AFFILIATE_STATUS } from "../../utils/constants/affiliateConstants";
import { getUserRole } from "../../utils/auth"; // Still used in line 97 for license validation
import { useGetAccountQuery } from "../../api/account";

function RootProfileNavBar2() {
  const dispatch = useDispatch();
  const { data: accountInfo, isSuccess } = useGetAccountQuery();
  const user = useSelector((state) => state.auth.user);

  // Use Redux data if available, otherwise fallback to RTK Query
  const preferences = user?.preferences || accountInfo?.preferences;
  const [notificationState, setNotificationState] = useState({
    isActive: false,
    unreadCount: 0,
    hasNewAvailableFilings: false,
  });
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const lastProcessedMessage = useRef(null);
  const dropdownRef = useRef(null);
  const values = useRouteLoaderData("root");

  // Get role from Redux to ensure it's reactive
  const role = useSelector((state) => state.auth.user?.user_role);

  useEffect(() => {
    // Don't connect until role is loaded
    if (!role) return;

    const handleNotification = (data) => {
      // Extract the actual notification from the message wrapper
      const notification = data?.message || data;

      // Don't update bell if user is on notifications page (they're already seeing the notifications)
      const isOnNotificationsPage =
        window.location.pathname === "/notifications";
      if (isOnNotificationsPage) {
        return;
      }

      // Check if user is currently on Available Filings page
      const isOnAvailableFilings =
        (window.location.pathname === "/filings" ||
          window.location.pathname === "/tax-filing") &&
        window.location.search.includes("segment=jobs");

      setNotificationState((prev) => ({
        isActive: true,
        unreadCount: prev.unreadCount + 1,
        // For CPA users, flag new available filings when a new_filing notification arrives
        // But only if they're not already on the Available Filings page
        hasNewAvailableFilings:
          role === "CPA" &&
          notification?.type === "new_filing" &&
          !isOnAvailableFilings
            ? true
            : prev.hasNewAvailableFilings,
      }));
    };

    // Single socket connection here
    const socketPath =
      role === "CPA" ? "/ws/filing/provider" : "/ws/filing/taxpayer";

    const socketService = new FilingNotificationService(socketPath);
    const subscription = socketService
      .getMessages()
      .subscribe(handleNotification);

    return () => subscription.unsubscribe();
  }, [role]); // Re-run when role changes

  // Click outside handler for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  // License validation notifications – show bell/top alert on all pages
  useEffect(() => {
    const role = getUserRole();
    if (role === "CPA") {
      const socketService = new LicenseValidationNotificationService();
      const subscription = socketService.getMessages().subscribe((message) => {
        if (message.type === "tax_expert_license_validation") {
          const messageId = `${message.decision}_${message.comment || ""}`;
          if (lastProcessedMessage.current === messageId) return;
          lastProcessedMessage.current = messageId;

          const isOnOnboard = location.pathname.includes("/onboard");
          const isApproved = message.decision === "Approved";
          const newStatus = isApproved ? "CA" : "LR";

          // Do not overwrite status to CA during onboarding; let the modal drive the transition
          if (!(isOnOnboard && isApproved)) {
            dispatch(updateUserStatus({ account_status: newStatus }));
          }

          // Only show alert/increment on onboard if rejected; elsewhere always
          const shouldNotify = isOnOnboard ? newStatus === "LR" : true;
          if (shouldNotify) {
            setNotificationState((prev) => ({
              isActive: true,
              unreadCount: prev.unreadCount + 1,
            }));
          }
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [location.pathname]);

  // Affiliate decision notifications – global for all users with pending applications
  useEffect(() => {
    const affiliateStatus = user?.affiliate_status;

    // Only connect if user has REV or REJ status (waiting for admin decision)
    if (
      affiliateStatus === AFFILIATE_STATUS.REV ||
      affiliateStatus === AFFILIATE_STATUS.REJ
    ) {
      const socketService = new AffiliateNotificationService();
      const subscription = socketService.getMessages().subscribe((message) => {
        if (message.type === "affiliate_notification") {
          // Map backend status codes to frontend constants
          const backendStatus = message.payload?.status;
          let newStatus;
          if (backendStatus === "AA") {
            newStatus = AFFILIATE_STATUS.CCP; // Approved → Need connected account
          } else if (backendStatus === "AR") {
            newStatus = AFFILIATE_STATUS.REJ; // Rejected
          }

          // Update Redux with mapped status
          if (newStatus) {
            dispatch(updateUserStatus({ affiliate_status: newStatus }));
          }

          // Show notification bell (only if not on notifications page)
          const isOnNotificationsPage =
            window.location.pathname === "/notifications";
          if (!isOnNotificationsPage) {
            setNotificationState((prev) => ({
              isActive: true,
              unreadCount: prev.unreadCount + 1,
              hasNewAvailableFilings: prev.hasNewAvailableFilings,
            }));
          }
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [user?.affiliate_status, dispatch]);

  return (
    <Fragment>
      {/* Top Navigation Bar */}
      <nav className="top-nav">
        <div className="top-nav-content">
          {/* Brand */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/burger_menu_white.svg`}
              alt="Burger"
              style={{ width: "32px", height: "32px" }}
              className="top-menu-burger"
              onClick={() => dispatch(toggleSidebar())}
            />
            {/**
             * <img
              src={`${process.env.PUBLIC_URL}/images/p_logo_white.svg`}
              alt="Burger"
              style={{ width: "32px", height: "32px" }}
              className="top-menu-burger"
            />
             */}

            <Link to="/" className="top-nav-brand">
              <svg
                height="30"
                viewBox="0 0 301 75"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
                style={{ borderRadius: 0 }}
              >
                <path d="M0.278932 73.5265L0.278933 3.65637L26.7298 27.6118L16.2493 51.5673V73.5265H0.278932ZM51.1554 26.3536C50.8714 20.2194 48.4914 15.0374 44.0975 10.6434C39.4062 5.95211 33.617 3.65637 26.7298 3.65637H3.25826L28.3198 26.3535L51.1554 26.3536ZM51.1743 28.3536C51.0029 34.714 48.6142 40.0635 44.0975 44.5803C39.4062 49.2715 33.617 51.5673 26.7298 51.5673H18.4322L28.5882 28.3535L51.1743 28.3536ZM74.1415 73.5265V0.661957H59.1694V73.5265H74.1415ZM121.33 28.3105C117.837 24.2182 112.846 22.2219 106.458 22.2219C99.8703 22.2219 94.2807 24.817 89.5894 29.9076C84.8981 34.9981 82.6023 41.1866 82.6023 48.5729C82.6023 55.9591 84.8981 62.2475 89.5894 67.338C94.2807 72.4286 99.8703 74.9239 106.458 74.9239C112.846 74.9239 117.837 72.9276 121.33 68.8353V73.5265H136.303V23.6193H121.33V28.3105ZM100.868 57.4563C98.6725 55.2604 97.5745 52.266 97.5745 48.5729C97.5745 44.8797 98.6725 41.8853 100.868 39.6894C103.064 37.4935 105.959 36.3955 109.453 36.3955C112.946 36.3955 115.841 37.4935 118.037 39.6894C120.232 41.8853 121.33 44.8797 121.33 48.5729C121.33 52.266 120.232 55.2604 118.037 57.4563C115.841 59.6523 112.946 60.7502 109.453 60.7502C105.959 60.7502 103.064 59.6523 100.868 57.4563ZM161.061 16.6323C164.655 13.0389 164.655 7.44933 161.061 3.95583C157.568 0.362509 151.978 0.362509 148.385 3.95583C144.891 7.44933 144.891 13.0389 148.385 16.6323C151.978 20.1258 157.568 20.1258 161.061 16.6323ZM162.259 73.5265V23.6193H147.287V73.5265H162.259ZM202.461 22.2219C195.973 22.2219 191.182 24.2182 188.187 28.3105V23.6193H173.215V73.5265H188.187V46.2771C188.187 39.4897 191.781 35.9962 197.47 35.9962C202.561 35.9962 206.154 39.1903 206.154 45.0793V73.5265H221.126V42.8834C221.126 36.3955 219.33 31.4048 215.736 27.7117C212.243 24.0185 207.751 22.2219 202.461 22.2219ZM262.35 24.6227H252.068V10.6487L237.096 15.1403V24.6227H229.111V38.9959H237.096V56.6631C237.096 63.9495 238.893 68.9403 242.586 71.8349C246.279 74.6297 252.867 75.5281 262.35 74.5299V60.9551C255.063 61.3544 252.068 60.6557 252.068 56.6631V38.9959H262.35V24.6227ZM285.307 23.6193H270.335V73.5265H285.307V50.9684C285.307 46.4767 286.904 43.2827 289.998 41.486C293.192 39.5896 296.586 38.9907 300.279 39.5896V22.6211C293.492 22.6211 287.203 26.1146 285.307 32.5027V23.6193Z" />
              </svg>
            </Link>
          </div>

          <div className="top-nav-actions">
            <div className="top-nav-icon-group">
              {location.pathname === "/" ? (
                <span
                  className="top-nav-icon active"
                  title="Home"
                  style={{ cursor: "default" }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/home.svg`}
                    alt="Home"
                    style={{ width: "28px", height: "28px" }}
                  />
                </span>
              ) : (
                <Link to="/" className="top-nav-icon" title="Home">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/home.svg`}
                    alt="Home"
                  />
                </Link>
              )}
              {location.pathname === "/notifications" ? (
                <span
                  className="top-nav-icon active"
                  title="Notifications"
                  style={{ cursor: "default" }}
                >
                  <img
                    src={
                      notificationState.isActive
                        ? `${process.env.PUBLIC_URL}/images/icons/notifications_active.svg`
                        : `${process.env.PUBLIC_URL}/images/icons/notifications.svg`
                    }
                    alt="Notifications"
                    style={{ width: "26px", height: "26px" }}
                  />
                </span>
              ) : (
                <Link
                  to="/notifications"
                  className="top-nav-icon"
                  title="Notifications"
                  onClick={() =>
                    setNotificationState((prev) => ({
                      ...prev,
                      isActive: false,
                    }))
                  }
                >
                  <img
                    src={
                      notificationState.isActive
                        ? `${process.env.PUBLIC_URL}/images/icons/notifications_active.svg`
                        : `${process.env.PUBLIC_URL}/images/icons/notifications.svg`
                    }
                    alt="Notifications"
                  />
                </Link>
              )}
              {/* <Link 
                to="/messages"
                className="top-nav-icon" 
                title="Messages"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/messages.svg`}
                  alt="Messages"
                  style={{ width: "20px", height: "20px" }}
                />
              </Link> */}
            </div>
            <div className="profile-dropdown-container" ref={dropdownRef}>
              <button
                className="profile-section"
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              >
                <img
                  src={
                    isSuccess && accountInfo?.personal_info?.avatar
                      ? accountInfo.personal_info.avatar
                      : `${process.env.PUBLIC_URL}/images/avatar.svg`
                  }
                  alt="Profile"
                  className="profile-avatar"
                />
              </button>

              {isProfileDropdownOpen && (
                <div className="profile-dropdown">
                  <h3 className="profile-dropdown-name">{user?.user_name}</h3>

                  <div className="profile-dropdown-section">
                    <h3 className="profile-dropdown-title">Account</h3>
                    <Link
                      to="account/settings"
                      className="profile-dropdown-link"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      Settings & Privacy
                    </Link>
                    {role === "CPA" && (
                      <Link
                        to="account/preferences"
                        className="profile-dropdown-link"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        Expert Preferences
                      </Link>
                    )}
                  </div>
                  <div className="profile-dropdown-section">
                    <h3 className="profile-dropdown-title">
                      Payments & Subscriptions
                    </h3>
                    <Link
                      to="account/transactions"
                      className="profile-dropdown-link"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      Payments & Payout
                    </Link>
                    <Link
                      to="account/subscriptions"
                      className="profile-dropdown-link"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      Subscriptions
                    </Link>
                  </div>

                  <div className="profile-dropdown-section">
                    <Form action="/logout" method="post">
                      <button type="submit" className="cta-button secondary">
                        Logout
                      </button>
                    </Form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      <Outlet
        context={{
          notificationState,
          clearNewAvailableFilings: () =>
            setNotificationState((prev) => ({
              ...prev,
              hasNewAvailableFilings: false,
            })),
        }}
      />
    </Fragment>
  );
}

export default RootProfileNavBar2;
